import { useState, useEffect } from 'react';
import api from '../services/api';
import SideDrawer from './SideDrawer';
import {
  CubeIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function PackingGuideDrawer({ open, onClose, quoteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchGuide = async () => {
    if (!quoteId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/auth/slicer-quote/${quoteId}/packing-guide`);
      setData(response.data.data);
    } catch (err) {
      console.error('Error fetching packing guide:', err);
      setError(err.response?.data?.error || 'Failed to load packing guide');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && quoteId) {
      setData(null);
      fetchGuide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when drawer opens
  }, [open, quoteId]);

  const formatWeight = (grams) => {
    if (grams >= 1000) return `${(grams / 1000).toFixed(2)} kg`;
    return `${Math.round(grams)} g`;
  };

  const formatCents = (cents) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  const renderLoading = () => (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto" />
        <p className="mt-4 text-sm text-slate-500">Generating packing guide...</p>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <ExclamationTriangleIcon className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-700 mb-3">{error}</p>
        <button
          onClick={fetchGuide}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Retry
        </button>
      </div>
    </div>
  );

  const renderGuide = () => {
    if (!data) return null;

    const { operatorGuide, boxBreakdown, totalCostInCents, pickupFeeInCents, warnings, totalBoxes, quantity } = data;

    return (
      <div className="p-6 space-y-6">
        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-indigo-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-indigo-700">{totalBoxes}</div>
            <div className="text-xs text-indigo-600">{totalBoxes === 1 ? 'Box' : 'Boxes'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-slate-700">{quantity}</div>
            <div className="text-xs text-slate-600">{quantity === 1 ? 'Unit' : 'Units'}</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-emerald-700">{formatCents(totalCostInCents)}</div>
            <div className="text-xs text-emerald-600">Est. Shipping</div>
          </div>
        </div>

        {/* Operator guide steps */}
        {operatorGuide && operatorGuide.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Packing Steps</h3>
            {operatorGuide.map((step) => {
              const breakdown = boxBreakdown?.find(b => b.boxSequence === step.boxSequence);
              return (
                <div
                  key={step.stepNumber}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Step header */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold">
                      {step.stepNumber}
                    </span>
                    <div className="flex-1">
                      <span className="font-medium text-slate-900">{step.boxName}</span>
                      {step.boxKey && (
                        <span className="ml-2 text-xs text-slate-400">{step.boxKey}</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      {step.itemCount} {step.itemCount === 1 ? 'item' : 'items'}
                    </span>
                  </div>

                  {/* Step body */}
                  <div className="px-4 py-3 space-y-2">
                    <p className="text-sm text-slate-700">{step.instruction}</p>

                    {/* Items breakdown */}
                    {step.items && step.items.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {step.items.map((item, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600"
                          >
                            {item.count > 1 ? `${item.count}x ` : ''}{item.quoteId.slice(-6)}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Box metrics */}
                    {breakdown && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                        <div>
                          <span className="text-slate-400">Billable:</span>{' '}
                          {formatWeight(breakdown.billableWeightGrams)}
                        </div>
                        <div>
                          <span className="text-slate-400">Vol:</span>{' '}
                          {Math.round((breakdown.volumeUtilization || 0) * 100)}%
                        </div>
                        <div>
                          <span className="text-slate-400">Carrier:</span>{' '}
                          {breakdown.tierName || '-'} — {formatCents(breakdown.carrierCostInCents)}
                        </div>
                        {breakdown.boxCostInCents > 0 && (
                          <div>
                            <span className="text-slate-400">Box cost:</span>{' '}
                            {formatCents(breakdown.boxCostInCents)}
                          </div>
                        )}
                        {breakdown.fuelSurchargeInCents > 0 && (
                          <div>
                            <span className="text-slate-400">Fuel:</span>{' '}
                            {formatCents(breakdown.fuelSurchargeInCents)}
                          </div>
                        )}
                        {breakdown.surchargesInCents > 0 && (
                          <div>
                            <span className="text-slate-400">Surcharges:</span>{' '}
                            {formatCents(breakdown.surchargesInCents)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step warnings */}
                    {step.warning && (
                      <div className="flex items-start gap-1.5 mt-1 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        {step.warning}
                      </div>
                    )}
                    {step.warnings && step.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-1.5 mt-1 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        {w}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pickup fee */}
        {pickupFeeInCents > 0 && (
          <div className="text-xs text-slate-500 text-right">
            Pickup fee: {formatCents(pickupFeeInCents)}
          </div>
        )}

        {/* Global warnings */}
        {warnings && warnings.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wide">Warnings</h3>
            {warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{w.message || w}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <CubeIcon className="h-5 w-5 text-indigo-600" />
          Packing Guide
        </span>
      }
    >
      {loading && renderLoading()}
      {error && !loading && renderError()}
      {data && !loading && !error && renderGuide()}
    </SideDrawer>
  );
}
